import React, { useState, useEffect } from "react";
import {
  useDeleteDocument,
  useCreateDocument,
  useUpdateDocument,
} from "../../Hooks/FetchHook";
import styles from "./CollectionManager.module.scss";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { AddDocumentForm } from "./AddDocumentForm/AddDocumentForm";
import { propertyInfoDefault } from "../../utils/constants";
import {
  useGetDocumentByDocumentId,
  useGetAllDocuments,
} from "../../Hooks/FetchHook";
import { Timestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "alertifyjs/build/css/alertify.min.css";
import "alertifyjs/build/css/themes/default.min.css";
import { storage } from "../../Firebase/Firebase";
import alertify from "alertifyjs";
import { deleteObject, ref } from "firebase/storage";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

interface CollectionManagerProps {
  activeCollection: string;
}

interface Document {
  id: string;
  [key: string]: any;
}

const CollectionManager: React.FC<CollectionManagerProps> = ({
  activeCollection,
}) => {
  const navigate = useNavigate();
  const { data: options, isLoading: isUbicationsLoading } =
    useGetDocumentByDocumentId("registrar", "opciones");
  const { data: propertiesFetch, isLoading: isPropertiesLoading } =
    useGetAllDocuments(activeCollection);
  const createDocumentMutation = useCreateDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const updateDocumentMutation = useUpdateDocument();

  const [properties, setProperties] = useState<Document[]>([]);
  const [searchTxt, setSearchTxt] = useState("");
  const [open, setOpen] = useState(false);
  const [numOfImage, setNumOfImage] = useState(1);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (!isPropertiesLoading) {
      setProperties(propertiesFetch ?? []);
    }
  }, [propertiesFetch, isPropertiesLoading]);

  const deleteDocument = async (id: string) => {
    try {
      //remove images from storage and then delete the document
      const docToDelete = properties.find((doc) => doc.id === id)?.imagenesURLs;
      if (docToDelete) {
        let i = 1;
        handleOpen();
        for await (const img of docToDelete) {
          const imageRef = ref(storage, img);
          await deleteObject(imageRef);
          setNumOfImage(i++);
        }
        handleClose();
      }
      deleteDocumentMutation.mutate(
        { collectionName: activeCollection, documentId: id },
        {
          onSuccess: () => {
            setProperties(properties.filter((doc) => doc.id !== id));
            alertify.success("Casa eliminada");
          },
        }
      );
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  };

  const handleDeleteClick = async (id: string) => {
    alertify.confirm(
      "MS Bienes Raices",
      "Desea eliminar esta casa?",
      function () {
        deleteDocument(id);
      },
      function () {
        alertify.error("Cancelado");
      }
    );
  };

  const handleAdd = async (doc: any) => {
    try {
      doc.created_at = Timestamp.now();
      createDocumentMutation.mutate(
        { collectionName: activeCollection, doc },
        {
          onSuccess: (id) => {
            setProperties([...properties, { ...doc, id: id }]);
            alertify.success("Casa agregada");
          },
        }
      );
    } catch (error) {
      console.error("Error adding document:", error);
    }
  };

  const handleCellEdit = async (
    updatedRow: Document,
    originalRow: Document
  ) => {
    try {
      const { id } = updatedRow;

      // Find what fields have changed
      const changedFields: any = {};
      Object.keys(updatedRow).forEach((key) => {
        if (updatedRow[key] !== originalRow[key]) {
          changedFields[key] = updatedRow[key];
        }
      });

      // If no fields changed, return the updated row
      if (Object.keys(changedFields).length === 0) {
        return updatedRow;
      }

      // Update the document in Firebase
      await updateDocumentMutation.mutateAsync({
        collectionName: activeCollection,
        documentId: id,
        data: changedFields,
      });

      // Update local state
      setProperties((prevProperties) =>
        prevProperties.map((property) =>
          property.id === id ? updatedRow : property
        )
      );

      alertify.success("Propiedad actualizada");
      return updatedRow;
    } catch (error) {
      console.error("Error updating document:", error);
      alertify.error("Error al actualizar la propiedad");
      // Return the original row to revert the change
      return originalRow;
    }
  };

  if (isUbicationsLoading || isPropertiesLoading) {
    return <div>Loading...</div>;
  }

  const handleRedirectToFichaTecnica = (id: string) => {
    const params = new URLSearchParams({
      propertyId: id,
    }).toString();
    navigate(`/fichaTecnica?${params}`);
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90, editable: false },
    {
      field: "titulo",
      headerName: "Titulo",
      type: "string",
      width: 150,
      editable: true,
    },
    {
      field: "direccion",
      headerName: "Direccion",
      type: "string",
      width: 150,
      editable: true,
    },
    {
      field: "habitaciones",
      headerName: "Habitaciones",
      type: "number",
      width: 80,
      editable: true,
    },
    {
      field: "banos",
      headerName: "Baños",
      type: "number",
      width: 80,
      editable: true,
    },
    {
      field: "tipoDePropiedad",
      headerName: "Tipo de propiedad",
      type: "string",
      width: 130,
      editable: false,
    },
    {
      field: "descripcion",
      headerName: "Descripcion",
      width: 350,
      type: "string",
      editable: true,
    },
    {
      field: "terreno",
      headerName: "Terreno",
      type: "number",
      width: 110,
      editable: true,
    },
    {
      field: "estacionamiento",
      headerName: "Estacionamiento",
      type: "number",
      width: 110,
      editable: true,
    },
    {
      field: "construccion",
      headerName: "Construccion",
      type: "number",
      width: 110,
      editable: true,
    },
    {
      field: "precio",
      headerName: "Precio",
      type: "number",
      width: 110,
      editable: true,
    },
    {
      field: "latitud",
      headerName: "Latitud",
      type: "string",
      width: 110,
      editable: true,
    },
    {
      field: "longitud",
      headerName: "Longitud",
      type: "string",
      width: 110,
      editable: true,
    },
    {
      field: "ciudad",
      headerName: "Ciudad",
      type: "string",
      width: 110,
      editable: false,
    },
    {
      field: "anoDeConstruccion",
      headerName: "Año de construccion",
      type: "number",
      width: 110,
      editable: true,
    },
    {
      field: "tipoDeNegocio",
      headerName: "Tipo de negocio",
      type: "number",
      width: 130,
      editable: false,
    },
    {
      field: "Delete",
      headerName: "Delete",
      width: 100,
      renderCell: (params) => {
        return (
          <div>
            <button onClick={() => handleDeleteClick(params.row.id as string)}>
              Delete
            </button>
          </div>
        );
      },
    },
    {
      field: "fichaTecnica",
      headerName: "Ficha Tecnica",
      width: 150,
      renderCell: (params) => {
        return (
          <div>
            <button
              onClick={() =>
                handleRedirectToFichaTecnica(params.row.id as string)
              }
            >
              Ficha Tecnica
            </button>
          </div>
        );
      },
    },
  ];
  return (
    <div className={styles.collectionManager}>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Por favor espera
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            Eliminando imagen numero {numOfImage}.
          </Typography>
        </Box>
      </Modal>
      <div className={styles.tableSection}>
        <div className={styles.searchBox}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTxt}
            onChange={(e) => setSearchTxt(e.target.value)}
          />
        </div>
        <DataGrid
          rows={properties}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
              },
            },
          }}
          pageSizeOptions={[50]}
          disableRowSelectionOnClick
          processRowUpdate={handleCellEdit}
        />
      </div>
      <div className={styles.formSection}>
        <h3>Agregar nueva propiedad</h3>
        <AddDocumentForm
          structure={propertyInfoDefault}
          onAdd={(doc) => handleAdd(doc)}
          ubications={options.Ubicacion}
          propertyTypes={options.Tipo}
          businessTypes={options.Servicio}
        />
      </div>
    </div>
  );
};

export default CollectionManager;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};
